import React, { useEffect, useRef } from "react";
import { Controller, useWatch } from "react-hook-form";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Divider,
  styled,
  Paper,
  Switch,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NotesIcon from "@mui/icons-material/Notes";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ScaleIcon from "@mui/icons-material/Scale";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { loadGoogleMaps } from "../../../../utils/loadGoogleMaps";
import { parseAddressComponents } from "../../../../utils/parseAddressComponents";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCities,
  fetchCountries,
  fetchStates,
} from "../../../../features/buyer/buyerSlice";

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)",
  borderRadius: "20px",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
  padding: "48px",
  maxWidth: "1200px",
  margin: "0 auto",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "24px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "2px solid rgba(0, 0, 0, 0.06)",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1a1a1a",
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.06)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(25, 118, 210, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
      borderWidth: "2px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)",
      backgroundColor: "#ffffff",
    },
  },
  "& .MuiInputBase-input": {
    padding: "16px 16px",
    fontFamily: "'Inter', sans-serif",
    fontWeight: "500",
    "&::placeholder": {
      opacity: "0.6",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Inter', sans-serif",
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "14px",
    transform: "translate(14px, -12px) scale(0.75)",
    backgroundColor: "#ffffff",
    paddingLeft: "6px",
    paddingRight: "6px",
    marginLeft: "-3px",
    "&.Mui-focused": {
      color: "primary.main",
    },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Inter', sans-serif",
    fontSize: "13px",
    marginLeft: "0",
    color: "#718096",
    marginTop: "8px",
  },
}));

const SectionTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "24px",
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "2px solid rgba(0, 0, 0, 0.05)",
  "&:first-of-type": {
    marginTop: "0",
    borderTop: "none",
    paddingTop: "0",
  },
});

const IconWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  borderRadius: "8px",
  backgroundColor: "rgba(25, 118, 210, 0.1)",
  color: "primary.main",
  "& svg": {
    width: "16px",
    height: "16px",
  },
});

const SectionLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "700",
  fontSize: "14px",
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "primary.main",
});

const HeadingBox = styled(Box)({
  marginBottom: "40px",
  paddingBottom: "32px",
  borderBottom: "2px solid rgba(0, 0, 0, 0.05)",
});

const FormGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const DocumentGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const FullWidthGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const FourColGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  marginBottom: "20px",
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const AssistanceBox = styled(Box)(({ theme }) => ({
  padding: "20px",
  borderRadius: "12px",
  background:
    "linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(25, 118, 210, 0.02) 100%)",
  border: "1px solid rgba(25, 118, 210, 0.1)",
  marginBottom: "24px",
  marginTop: "24px",
  display: "flex",
  // alignItems: "center",
  flexDirection: "column",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    borderColor: "rgba(25, 118, 210, 0.2)",
    backgroundColor: "rgba(25, 118, 210, 0.06)",
  },
}));

const NotesBox = styled(Box)({
  marginBottom: "8px",
});

const headingStyles = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "800",
  fontSize: "30px",
  color: "#0a0e27",
  marginBottom: "12px",
  background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
});

const subheadingStyles = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "15px",
  fontWeight: "500",
  color: "#718096",
  lineHeight: "1.6",
});

export function SellerToShipForm({ control, errors, setValue }) {
  const dispatch = useDispatch();

  const { countries, delivery, billing, loading } = useSelector(
    (state) => state.buyer.addressSelector,
  );

  const deliveryCountry = useWatch({
    control,
    name: "deliveryAddress.country",
  });

  const deliveryState = useWatch({
    control,
    name: "deliveryAddress.state",
  });

  const billingCountry = useWatch({
    control,
    name: "billingAddress.country",
  });

  const billingState = useWatch({
    control,
    name: "billingAddress.state",
  });

  const today = new Date().toISOString().split("T")[0];
  const startDate = useWatch({
    control,
    name: "deliveryAddress.startDate",
  });

  // Load countries once
  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);
  // console.log("Countries:", countries);
  // Country → States
  useEffect(() => {
    if (deliveryCountry) {
      dispatch(fetchStates({ countryId: deliveryCountry, type: "delivery" }));
      setValue("deliveryAddress.state", "");
      setValue("deliveryAddress.city", "");
    }
  }, [deliveryCountry, dispatch, setValue]);
  // console.log("States:", delivery.states);

  // State → Cities
  useEffect(() => {
    if (deliveryState) {
      dispatch(fetchCities({ stateId: deliveryState, type: "delivery" }));
      setValue("deliveryAddress.city", "");
    }
  }, [deliveryState, dispatch, setValue]);

  useEffect(() => {
    if (billingCountry) {
      dispatch(fetchStates({ countryId: billingCountry, type: "billing" }));

      setValue("billingAddress.state", "");
      setValue("billingAddress.city", "");
    }
  }, [billingCountry, dispatch, setValue]);

  useEffect(() => {
    if (billingState) {
      dispatch(fetchCities({ stateId: billingState, type: "billing" }));
      setValue("billingAddress.city", "");
    }
  }, [billingState, dispatch, setValue]);

  return (
    <>
      <StyledPaper elevation={0} style={{ marginTop: 10 }}>
        <HeadingBox>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "800",
              fontSize: "30px",
              background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              marginBottom: "12px",
            }}
          >
            Delivery Address
          </Typography>

          <Typography
            sx={{ color: "#718096", fontSize: "15px", fontWeight: 500 }}
          >
            Provide delivery location details for drop-off
          </Typography>
        </HeadingBox>

        {/* CONTACT */}
        <SectionTitle>
          <IconWrapper>
            <PersonIcon />
          </IconWrapper>
          <SectionLabel>Contact Information</SectionLabel>
        </SectionTitle>

        <FormGrid>
          <Controller
            name="deliveryAddress.firstName"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="First Name *"
                size="small"
                error={!!errors?.deliveryAddress?.firstName}
                helperText={errors?.deliveryAddress?.firstName?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.lastName"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Last Name *"
                size="small"
                error={!!errors?.deliveryAddress?.lastName}
                helperText={errors?.deliveryAddress?.lastName?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.phone"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Phone *"
                size="small"
                error={!!errors?.deliveryAddress?.phone}
                helperText={errors?.deliveryAddress?.phone?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.companyName"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Company Name"
                size="small"
                error={!!errors?.deliveryAddress?.companyName}
                helperText={errors?.deliveryAddress?.companyName?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.taxId"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="ABN/ACN/VAT/Tax ID *"
                size="small"
                error={!!errors?.deliveryAddress?.taxId}
                helperText={errors?.deliveryAddress?.taxId?.message}
              />
            )}
          />
        </FormGrid>

        {/* LOCATION */}
        <SectionTitle>
          <IconWrapper>
            <LocationOnIcon />
          </IconWrapper>
          <SectionLabel>Location Details</SectionLabel>
        </SectionTitle>

        <FormGrid>
          <Controller
            name="deliveryAddress.country"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="Country *"
                size="small"
                error={!!errors?.deliveryAddress?.country}
                helperText={errors?.deliveryAddress?.country?.message}
              >
                <MenuItem value="">Select country</MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          />
          <Controller
            name="deliveryAddress.state"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="State *"
                size="small"
                error={!!errors?.deliveryAddress?.state}
                helperText={errors?.deliveryAddress?.state?.message}
              >
                <MenuItem value="">Select state</MenuItem>
                {delivery.states.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          />
          <Controller
            name="deliveryAddress.city"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="City *"
                size="small"
                error={!!errors?.deliveryAddress?.city}
                helperText={errors?.deliveryAddress?.city?.message}
              >
                <MenuItem value="">Select city</MenuItem>
                {delivery.cities.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          />
          <Controller
            name="deliveryAddress.postcode"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Postcode *"
                size="small"
                error={!!errors?.deliveryAddress?.postcode}
                helperText={errors?.deliveryAddress?.postcode?.message}
              />
            )}
          />
        </FormGrid>

        {/* ADDRESS */}
        <SectionTitle>
          <IconWrapper>
            <HomeIcon />
          </IconWrapper>
          <SectionLabel>Address</SectionLabel>
        </SectionTitle>

        <FullWidthGrid>
          <Controller
            name="deliveryAddress.addressLine1"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Address Line 1 *"
                size="small"
                error={!!errors?.deliveryAddress?.addressLine1}
                helperText={errors?.deliveryAddress?.addressLine1?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.addressLine2"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Address Line 2"
                size="small"
                error={!!errors?.billingAddress?.addressLine2}
                helperText={errors?.billingAddress?.addressLine2?.message}
              />
            )}
          />
        </FullWidthGrid>

        <SectionTitle>
          <IconWrapper>
            <LocalShippingIcon />
          </IconWrapper>
          <SectionLabel>Logistics & Equipment</SectionLabel>
        </SectionTitle>
        <FormGrid>
          <Controller
            name="deliveryAddress.locationType"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="Location Type"
                SelectProps={{ native: true }}
                size="small"
                error={!!errors?.deliveryAddress?.locationType}
                helperText={errors?.deliveryAddress?.locationType?.message}
              >
                <option value="">Select location type</option>
                <option value="warehouse">Warehouse</option>
                <option value="retail">Retail Store</option>
                <option value="office">Office</option>
                <option value="residential">Residential</option>
              </StyledTextField>
            )}
          />
          <Controller
            name="deliveryAddress.forkliftAvailability"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value === 1}
                    onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                  />
                }
                label="Forklift Available"
                error={!!errors?.deliveryAddress?.forklifAvailable}
                helperText={errors?.deliveryAddress?.forklifAvailable?.message}
              />
            )}
          />
        </FormGrid>
        <AssistanceBox>
          <Controller
            name="deliveryAddress.noAssistanceAtDelivery" // Keep the name for react-hook-form
            control={control}
            render={({ field }) => (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value === 1} // Ensure it's using the correct value
                      onChange={(e) => {
                        field.onChange(e.target.checked ? 1 : 0); // Handle onChange to update the state
                      }}
                      sx={{
                        "& .MuiSwitch-switchBase": {
                          color: "#cbd5e0",
                          "&.Mui-checked": {
                            color: "primary.main",
                            "& + .MuiSwitch-track": {
                              backgroundColor: "primary.main",
                            },
                          },
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#cbd5e0",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography fontWeight={600}>
                      {field.value === 1
                        ? "Assistance Available"
                        : "No Assistance Available"}
                    </Typography>
                  }
                />

                {/* Conditionally render the Special Assistance Notes field */}
                {field.value === 1 && (
                  <>
                    <SectionTitle>
                      <IconWrapper>
                        <NotesIcon />
                      </IconWrapper>
                      <SectionLabel>Special Assistance Notes</SectionLabel>
                    </SectionTitle>
                    <Controller
                      name="deliveryAddress.specialAssistanceNotes"
                      control={control}
                      shouldUnregister
                      render={({ field: notesField }) => (
                        <StyledTextField
                          {...notesField}
                          fullWidth
                          multiline
                          rows={7}
                          label="Special Assistance Notes (Optional)"
                          size="small"
                          error={!!errors?.deliveryAddress?.specialNotes}
                          helperText={
                            errors?.deliveryAddress?.specialNotes?.message
                          }
                        />
                      )}
                    />
                  </>
                )}
              </>
            )}
          />
        </AssistanceBox>

        {/* DELIVERY TIME SLOT */}
        <SectionTitle>
          <IconWrapper>
            <DescriptionIcon />
          </IconWrapper>
          <SectionLabel>Delivery Time Slot</SectionLabel>
        </SectionTitle>
        <FormGrid>
          <Controller
            name="deliveryAddress.startDate"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{
                  min: today,
                }}
                error={!!errors?.deliveryAddress?.startDate}
                helperText={errors?.deliveryAddress?.startDate?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.endDate"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{
                  min: startDate || today,
                }}
                error={!!errors?.deliveryAddress?.endDate}
                helperText={errors?.deliveryAddress?.endDate?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.deliveryTimeSlot"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="Delivery Time Slot"
                SelectProps={{ native: true }}
                size="small"
                error={!!errors?.deliveryAddress?.timeSlot}
                helperText={errors?.deliveryAddress?.timeSlot?.message}
              >
                <option value="">Select time slot</option>
                <option value="08:00-10:00">08:00 AM - 10:00 AM</option>
                <option value="10:00-12:00">10:00 AM - 12:00 PM</option>
                <option value="12:00-14:00">12:00 PM - 2:00 PM</option>
                <option value="14:00-16:00">2:00 PM - 4:00 PM</option>
                <option value="16:00-18:00">4:00 PM - 6:00 PM</option>
              </StyledTextField>
            )}
          />
        </FormGrid>

        {/* NOTES */}
        <SectionTitle>
          <IconWrapper>
            <NotesIcon />
          </IconWrapper>
          <SectionLabel>Delivery Notes</SectionLabel>
        </SectionTitle>

        <NotesBox>
          <Box
            sx={{
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: "rgba(255,193,7,0.08)",
              border: "1px solid rgba(255,193,7,0.3)",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
              mb: 5,
            }}
          >
            {/* ICON */}
            <Box
              sx={{
                width: "28px",
                height: "28px",
                minWidth: "28px",
                borderRadius: "6px",
                backgroundColor: "rgba(255,193,7,0.25)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: "2px",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 20, color: "#b7791f" }} />
            </Box>

            {/* TEXT CONTENT */}
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "15px",
                  mb: 0.5,
                  color: "#8a6d3b",
                }}
              >
                Delivery Notes — What to include
              </Typography>

              {/* 2-COLUMN LIST */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  rowGap: "8px",
                  columnGap: "16px",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Unloading restrictions
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Location access details
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Preferred delivery timings
                </Typography>
              </Box>
            </Box>
          </Box>

          <Controller
            name="deliveryAddress.deliveryNotes"
            control={control}
            render={({ field: notesField }) => (
              <StyledTextField
                {...notesField}
                fullWidth
                multiline
                rows={7}
                label="Delivery Notes (Important) *"
                size="small"
                error={!!errors?.deliveryAddress?.deliveryNotes}
                helperText={errors?.deliveryAddress?.deliveryNotes?.message}
              />
            )}
          />
        </NotesBox>

        {/* FILE UPLOADS */}
        <SectionTitle>
          <IconWrapper>
            <NotesIcon />
          </IconWrapper>
          <SectionLabel>Delivery Images/Documents</SectionLabel>
        </SectionTitle>

        <FullWidthGrid
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Box
            sx={{
              border: "2px dashed #e5e7eb",
              borderRadius: 1,
              p: 2,
              textAlign: "center",
              backgroundColor: "#f9fafb",
            }}
          >
            <CloudUpload sx={{ fontSize: 32, color: "#9ca3af", mb: 1 }} />
            <Typography variant="body2">
              Drag files or click to upload
            </Typography>
          </Box>

          <SectionLabel>Documents Upload</SectionLabel>
          <Box
            sx={{
              border: "2px dashed #e5e7eb",
              borderRadius: 1,
              p: 2,
              textAlign: "center",
              backgroundColor: "#f9fafb",
            }}
          >
            <CloudUpload sx={{ fontSize: 32, color: "#9ca3af", mb: 1 }} />
            <Typography variant="body2">
              Drag files or click to upload
            </Typography>
          </Box>
        </FullWidthGrid>
      </StyledPaper>

      {/* BILLING ADDRESS */}
      <StyledPaper elevation={0} style={{ marginTop: 10 }}>
        {/* BILLING ADDRESS */}
        <Box>
          <HeadingBox>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "800",
                fontSize: "30px",
                background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginBottom: "12px",
              }}
            >
              Billing Address
            </Typography>

            <Typography
              sx={{ color: "#718096", fontSize: "15px", fontWeight: 500 }}
            >
              Required for every order
            </Typography>
          </HeadingBox>

          <SectionTitle>
            <IconWrapper>
              <PersonIcon />
            </IconWrapper>
            <SectionLabel>Contact Information</SectionLabel>
          </SectionTitle>
          <FormGrid>
            <Controller
              name="billingAddress.firstName"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="First Name *"
                  size="small"
                  error={!!errors?.billingAddress?.firstName}
                  helperText={errors?.billingAddress?.firstName?.message}
                />
              )}
            />

            <Controller
              name="billingAddress.lastName"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Last Name *"
                  size="small"
                  error={!!errors?.billingAddress?.lastName}
                  helperText={errors?.billingAddress?.lastName?.message}
                />
              )}
            />

            <Controller
              name="billingAddress.phone"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Phone *"
                  size="small"
                  error={!!errors?.billingAddress?.phone}
                  helperText={errors?.billingAddress?.phone?.message}
                />
              )}
            />

            <Controller
              name="billingAddress.email"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Email *"
                  type="email"
                  size="small"
                  error={!!errors?.billingAddress?.email}
                  helperText={errors?.billingAddress?.email?.message}
                />
              )}
            />
            <Controller
              name="billingAddress.companyName"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Company Name *"
                  size="small"
                  error={!!errors?.billingAddress?.companyName}
                  helperText={errors?.billingAddress?.companyName?.message}
                />
              )}
            />
            <Controller
              name="billingAddress.taxId"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="ABN/ACN/VAT/Tax ID *"
                  size="small"
                  error={!!errors?.billingAddress?.taxId}
                  helperText={errors?.billingAddress?.taxId?.message}
                />
              )}
            />
          </FormGrid>

          <SectionTitle>
            <IconWrapper>
              <LocationOnIcon />
            </IconWrapper>
            <SectionLabel>Location Details</SectionLabel>
          </SectionTitle>

          <FormGrid>
            <Controller
              name="billingAddress.country"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  select
                  fullWidth
                  label="Country *"
                  size="small"
                  error={!!errors?.billingAddress?.country}
                  helperText={errors?.billingAddress?.country?.message}
                >
                  <MenuItem value="">Select country</MenuItem>
                  {countries.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </StyledTextField>
              )}
            />

            <Controller
              name="billingAddress.state"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  select
                  fullWidth
                  label="State *"
                  size="small"
                  error={!!errors?.billingAddress?.state}
                  helperText={errors?.billingAddress?.state?.message}
                >
                  <MenuItem value="">Select state</MenuItem>
                  {billing.states.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </StyledTextField>
              )}
            />

            <Controller
              name="billingAddress.city"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  select
                  fullWidth
                  label="City *"
                  size="small"
                  error={!!errors?.billingAddress?.city}
                  helperText={errors?.billingAddress?.city?.message}
                >
                  <MenuItem value="">Select city</MenuItem>
                  {billing.cities.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </StyledTextField>
              )}
            />

            <Controller
              name="billingAddress.postcode"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Postcode *"
                  size="small"
                  error={!!errors?.billingAddress?.postcode}
                  helperText={errors?.billingAddress?.postcode?.message}
                />
              )}
            />
          </FormGrid>

          <SectionTitle>
            <IconWrapper>
              <HomeIcon />
            </IconWrapper>
            <SectionLabel>Billing Address</SectionLabel>
          </SectionTitle>
          <FormGrid>
            <Controller
              name="billingAddress.addressLine1"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Address Line 1 *"
                  size="small"
                  error={!!errors?.billingAddress?.addressLine1}
                  helperText={errors?.billingAddress?.addressLine1?.message}
                />
              )}
            />

            <Controller
              name="billingAddress.addressLine2"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Address Line 2"
                  size="small"
                  error={!!errors?.billingAddress?.addressLine2}
                  helperText={errors?.billingAddress?.addressLine2?.message}
                />
              )}
            />
            <Controller
              name="billingAddress.landmark"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Landmark"
                  size="small"
                  error={!!errors?.billingAddress?.landMark}
                  helperText={errors?.billingAddress?.landMark?.message}
                />
              )}
            />
          </FormGrid>
        </Box>
      </StyledPaper>
    </>
  );
}
